import React, { useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import {
  Headphones,
  Shield,
  Users,
  Monitor,
  FileText,
  ArrowRight,
  CheckCircle,
  Briefcase,
  GraduationCap,
  Heart,
  TrendingUp,
  DollarSign,
  Gift,
  Trophy,
  Utensils,
  Coffee,
  Smile,
  Plane,
  Sparkles,
  Zap,
  Star,
} from "lucide-react";

const Careers = () => {
  const observerRef = useRef(null);

  const positions = [
    {
      icon: Headphones,
      title: "Call Center Agents",
      department: "Operations",
      type: "Full-time",
      description:
        "Handle customer inquiries, provide support, and deliver exceptional service.",
    },
    {
      icon: Shield,
      title: "QA Inspectors",
      department: "Quality Assurance",
      type: "Full-time",
      description:
        "Monitor calls, evaluate performance, and maintain service quality standards.",
    },
    {
      icon: Users,
      title: "Supervisors",
      department: "Management",
      type: "Full-time",
      description:
        "Lead teams, manage operations, and ensure targets are achieved.",
    },
    {
      icon: Monitor,
      title: "IT Support",
      department: "Technology",
      type: "Full-time",
      description:
        "Maintain systems, troubleshoot issues, and support technical infrastructure.",
    },
    {
      icon: FileText,
      title: "Back Office Staff",
      department: "Operations",
      type: "Full-time",
      description:
        "Handle data entry, document processing, and administrative tasks.",
    },
  ];

  const benefits = [
    {
      icon: Briefcase,
      title: "Career Growth",
      description: "Clear advancement paths and promotion opportunities.",
    },
    {
      icon: GraduationCap,
      title: "Training Programs",
      description: "Comprehensive training and skill development.",
    },
    {
      icon: Heart,
      title: "Health Benefits",
      description: "Medical coverage and wellness programs.",
    },
    {
      icon: TrendingUp,
      title: "Performance Bonuses",
      description: "Competitive incentives based on performance.",
    },
  ];

  const perks = [
    "Competitive salary packages",
    "Friendly work environment",
    "Team building activities",
    "Recognition programs",
    "Flexible scheduling",
    "Professional development",
  ];

  const sevenReasons = [
    {
      icon: DollarSign,
      title: "Market-competitive salary",
    },
    {
      icon: Gift,
      title: "Daily bonuses and performance incentives",
    },
    {
      icon: Trophy,
      title: "Recognition and rewards for top performers",
    },
    {
      icon: Utensils,
      title: "In-house complimentary weekly dinner",
    },
    {
      icon: Coffee,
      title: "Complimentary one-time tea",
    },
    {
      icon: Smile,
      title: "Stress-free and respectful environment",
    },
    {
      icon: Plane,
      title: "Company-sponsored yearly trips & tour",
    },
  ];

  useEffect(() => {
    observerRef.current = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("active");
          }
        });
      },
      { threshold: 0.1 }
    );

    document.querySelectorAll(".reveal, .slide-left, .slide-right, .zoom-reveal, .flip-in, .blur-in, .rotate-in").forEach((el) => {
      observerRef.current.observe(el);
    });

    return () => observerRef.current?.disconnect();
  }, []);

  return (
    <div data-testid="careers-page" className="pt-20">
      {/* Hero Section */}
      <section 
        className="py-24 relative overflow-hidden"
        style={{
          backgroundImage: `url('/images/entrance.jpg')`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
      >
        <div className="absolute inset-0 bg-gradient-to-r from-[#353537]/95 to-[#353537]/70" />
        
        {/* Animated particles */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="particle" style={{ top: '15%', left: '15%', animationDelay: '0s' }} />
          <div className="particle" style={{ top: '25%', left: '75%', animationDelay: '1.5s' }} />
          <div className="particle" style={{ top: '65%', left: '25%', animationDelay: '3s' }} />
          <div className="particle" style={{ top: '75%', left: '80%', animationDelay: '0.5s' }} />
        </div>
        
        {/* Floating decorations */}
        <div className="absolute top-20 right-20 animate-float opacity-30">
          <Sparkles className="w-10 h-10 text-[#6DB432] animate-spin" style={{ animationDuration: '8s' }} />
        </div>
        <div className="absolute bottom-20 left-20 animate-float opacity-25" style={{ animationDelay: '2s' }}>
          <Star className="w-8 h-8 text-[#8BC064] heartbeat" />
        </div>
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="max-w-3xl stagger-children">
            <span className="text-[#6DB432] font-bold text-sm uppercase tracking-widest inline-flex items-center gap-2 bouncy-entrance">
              <span className="w-2 h-2 rounded-full bg-[#6DB432] animate-pulse" />
              Careers
              <span className="w-8 h-px bg-gradient-to-r from-[#6DB432] to-transparent" />
            </span>
            <h1 className="mt-4 text-4xl sm:text-5xl lg:text-6xl font-extrabold text-white leading-tight text-reveal">
              Grow With
              <br />
              <span className="text-[#6DB432] gradient-text-animated neon-text">NUVEXA</span>
            </h1>
            <p className="mt-4 text-[#6DB432] text-xl font-bold tracking-widest uppercase text-glow">
              Powered by PRECISION
            </p>
            <p className="mt-6 text-gray-300 text-lg sm:text-xl leading-relaxed">
              Join a team that values excellence, innovation, and professional
              growth. Build your career with one of the leading BPO companies.
            </p>
            <Link to="/apply" className="inline-block mt-8">
              <Button
                data-testid="careers-apply-btn"
                className="bg-[#6DB432] hover:bg-[#8BC064] text-white font-bold px-8 py-4 text-lg rounded-md btn-glow btn-shine jello-hover animate-pulse-glow"
              >
                Apply Now
                <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-2 transition-transform" />
              </Button>
            </Link>
          </div>
        </div>
        
        {/* Morphing decoration */}
        <div className="absolute top-1/2 right-0 w-96 h-96 bg-gradient-to-br from-[#6DB432]/10 to-[#8BC064]/5 -translate-y-1/2 morph-shape" />
      </section>

      {/* Open Positions */}
      <section className="py-24 bg-[#0f0f10] relative overflow-hidden">
        {/* Decorative X mark */}
        <img
          src="/images/x-mark.png"
          alt=""
          className="absolute -right-16 bottom-10 h-[450px] w-auto opacity-[0.12] pointer-events-none object-contain floating-x"
        />
        
        {/* Animated background grid */}
        <div className="absolute inset-0 opacity-5">
          <div className="absolute inset-0" style={{ backgroundImage: 'linear-gradient(#6DB432 1px, transparent 1px), linear-gradient(90deg, #6DB432 1px, transparent 1px)', backgroundSize: '50px 50px' }} />
        </div>
        
        {/* Spotlight sweep */}
        <div className="absolute inset-0 spotlight-sweep opacity-20" />
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="text-center mb-16 zoom-reveal">
            <span className="text-[#6DB432] font-bold text-sm uppercase tracking-widest inline-flex items-center gap-2 justify-center">
              <Zap className="w-4 h-4 heartbeat" />
              Join Our Team
              <Zap className="w-4 h-4 heartbeat" style={{ animationDelay: '0.5s' }} />
            </span>
            <h2 className="mt-4 text-3xl sm:text-4xl font-bold text-white">
              Open <span className="gradient-text-animated">Positions</span>
            </h2>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {positions.map((position, index) => (
              <div
                key={position.title}
                data-testid={`position-${index}`}
                className="flip-in glass-card border border-white/5 hover:border-[#6DB432]/30 p-6 rounded-xl pop-out group"
                style={{ transitionDelay: `${index * 0.1}s` }}
              >
                <div className="flex items-start justify-between mb-4">
                  <div className="w-12 h-12 rounded-lg bg-[#6DB432]/20 flex items-center justify-center group-hover:bg-[#6DB432]/30 transition-all duration-300">
                    <position.icon className="w-6 h-6 text-[#6DB432]" />
                  </div>
                  <span className="text-xs font-medium text-[#6DB432] bg-[#6DB432]/10 px-3 py-1 rounded-full floating-badge">
                    {position.type}
                  </span>
                </div>
                <h3 className="text-lg font-bold text-white mb-1 group-hover:text-[#6DB432] transition-colors duration-300 underline-grow">
                  {position.title}
                </h3>
                <p className="text-[#6DB432] text-sm mb-3">
                  {position.department}
                </p>
                <p className="text-gray-400 text-sm leading-relaxed mb-4 group-hover:text-gray-300 transition-colors duration-300">
                  {position.description}
                </p>
                <Link to="/apply">
                  <Button
                    data-testid={`apply-${position.title.toLowerCase().replace(/\s+/g, "-")}`}
                    variant="outline"
                    className="w-full border-[#6DB432]/50 text-[#6DB432] hover:bg-[#6DB432] hover:text-white font-medium text-sm bg-transparent btn-shine elastic-scale"
                  >
                    Apply for this Position
                  </Button>
                </Link>
                
                {/* Shimmer effect */}
                <div className="absolute inset-0 rounded-xl overflow-hidden pointer-events-none">
                  <div className="absolute inset-0 bg-gradient-to-r from-transparent via-[#6DB432]/5 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-1000" />
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-24 bg-[#353537]/30 relative overflow-hidden">
        {/* Floating decorations */}
        <div className="absolute top-10 left-10 w-20 h-20 border border-[#6DB432]/10 rounded-full float-card opacity-30" />
        <div className="absolute bottom-10 right-10 w-16 h-16 border border-[#8BC064]/10 rounded-lg float-card opacity-30" style={{ animationDelay: '2s' }} />
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="text-center mb-16 blur-in">
            <span className="text-[#6DB432] font-bold text-sm uppercase tracking-widest inline-flex items-center gap-2 justify-center">
              <Gift className="w-4 h-4 animate-bounce" />
              What We Offer
            </span>
            <h2 className="mt-4 text-3xl sm:text-4xl font-bold text-white">
              Employee <span className="gradient-text-animated">Benefits</span>
            </h2>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {benefits.map((benefit, index) => (
              <div
                key={benefit.title}
                data-testid={`benefit-card-${index}`}
                className="rotate-in text-center p-6 rounded-xl glass-card border border-white/5 hover:border-[#6DB432]/30 pop-out group"
                style={{ transitionDelay: `${index * 0.1}s` }}
              >
                <div className="w-14 h-14 rounded-full bg-[#6DB432]/20 flex items-center justify-center mx-auto mb-4 group-hover:bg-[#6DB432]/40 transition-all duration-500 group-hover:scale-110 icon-spin">
                  <benefit.icon className="w-7 h-7 text-[#6DB432]" />
                </div>
                <h3 className="text-white font-bold mb-2 group-hover:text-[#6DB432] transition-colors duration-300">{benefit.title}</h3>
                <p className="text-gray-400 text-sm group-hover:text-gray-300 transition-colors duration-300">{benefit.description}</p>
                
                {/* Pulse ring on hover */}
                <div className="absolute inset-0 rounded-xl border-2 border-[#6DB432]/0 group-hover:border-[#6DB432]/20 group-hover:animate-ping pointer-events-none" style={{ animationDuration: '2s' }} />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 7 Reasons to Choose NUVEXA */}
      <section className="py-24 bg-[#0f0f10] relative overflow-hidden">
        {/* Decorative X marks */}
        <img
          src="/images/x-mark.png"
          alt=""
          className="absolute -left-20 top-1/2 -translate-y-1/2 h-[400px] w-auto opacity-[0.08] pointer-events-none object-contain floating-x"
        />
        <img
          src="/images/x-mark.png"
          alt=""
          className="absolute -right-20 bottom-20 h-[300px] w-auto opacity-[0.06] pointer-events-none object-contain floating-x"
          style={{ animationDelay: '1s' }}
        />
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            {/* Left side - Big 7 */}
            <div className="reveal text-center lg:text-left">
              <div className="inline-flex items-center justify-center lg:justify-start">
                <div className="relative">
                  {/* Animated rings */}
                  <div className="absolute inset-0 w-48 h-48 md:w-60 md:h-60 rounded-full border-4 border-[#6DB432]/20 animate-ping" style={{ animationDuration: '3s' }} />
                  <div className="absolute inset-0 w-48 h-48 md:w-60 md:h-60 rounded-full border-2 border-[#8BC064]/30 animate-spin" style={{ animationDuration: '10s' }} />
                  {/* Main circle */}
                  <div className="relative w-48 h-48 md:w-60 md:h-60 rounded-full border-4 border-[#6DB432] flex items-center justify-center bg-gradient-to-br from-[#6DB432]/10 to-[#1a1a1b] shadow-2xl shadow-[#6DB432]/20">
                    <span className="text-8xl md:text-9xl font-black text-[#6DB432] animate-pulse stats-glow" style={{ animationDuration: '2s' }}>7</span>
                  </div>
                </div>
              </div>
              <div className="mt-8">
                <p className="text-2xl md:text-3xl font-bold text-white">
                  REASONS
                </p>
                <p className="text-3xl md:text-4xl font-extrabold">
                  <span className="text-white">To </span>
                  <span className="text-[#6DB432]">Choose</span>
                </p>
                <p className="text-3xl md:text-4xl font-extrabold text-white">
                  NUVEXA
                </p>
              </div>
            </div>

            {/* Right side - Reasons list with staggered animation */}
            <div className="space-y-7">
              {sevenReasons.map((reason, index) => (
                <div
                  key={reason.title}
                  data-testid={`reason-${index}`}
                  className="reveal flex items-center gap-5 group animate-slideInRight"
                  style={{ 
                    transitionDelay: `${index * 0.1}s`,
                    animationDelay: `${index * 0.15}s`
                  }}
                >
                  {/* Animated number indicator */}
                  <div className="w-10 h-10 rounded-full bg-[#6DB432]/20 border border-[#6DB432]/40 flex items-center justify-center flex-shrink-0 group-hover:bg-[#6DB432] transition-all duration-300">
                    <span className="text-[#6DB432] font-bold group-hover:text-white transition-colors duration-300">{index + 1}</span>
                  </div>
                  
                  {/* Reason card - Dark transparent background */}
                  <div className="flex-1 bg-[#353537]/60 backdrop-blur-sm border border-[#6DB432]/20 rounded-2xl px-6 py-5 group-hover:border-[#6DB432]/50 group-hover:bg-[#353537]/80 transition-all duration-500 group-hover:-translate-y-1 group-hover:translate-x-2 relative overflow-hidden shadow-lg">
                    {/* Shimmer effect */}
                    <div className="absolute inset-0 bg-gradient-to-r from-transparent via-[#6DB432]/10 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-1000" />
                    <p className="text-white font-semibold text-sm md:text-base relative z-10">
                      {reason.title}
                    </p>
                  </div>
                  
                  {/* Icon circle */}
                  <div className="w-14 h-14 md:w-16 md:h-16 rounded-2xl bg-gradient-to-br from-[#6DB432] to-[#8BC064] flex items-center justify-center flex-shrink-0 group-hover:scale-110 group-hover:rotate-6 transition-all duration-500 shadow-lg shadow-[#6DB432]/30 group-hover:shadow-xl group-hover:shadow-[#6DB432]/40">
                    <reason.icon className="w-7 h-7 md:w-8 md:h-8 text-white" />
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Perks Grid */}
      <section className="py-24 bg-[#353537]/30 relative overflow-hidden">
        {/* Animated neon lines */}
        <div className="absolute top-0 left-1/4 w-px h-full bg-gradient-to-b from-transparent via-[#6DB432]/20 to-transparent" />
        <div className="absolute top-0 right-1/4 w-px h-full bg-gradient-to-b from-transparent via-[#8BC064]/15 to-transparent" />
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div className="slide-left">
              <span className="text-[#6DB432] font-bold text-sm uppercase tracking-widest inline-flex items-center gap-2">
                <Users className="w-4 h-4 animate-bounce" />
                Work Culture
              </span>
              <h2 className="mt-4 text-3xl sm:text-4xl font-bold text-white leading-tight">
                Why Work at
                <br />
                <span className="text-[#6DB432] gradient-text-animated">NUVEXA?</span>
              </h2>
              <p className="mt-6 text-gray-400 leading-relaxed hover:text-gray-300 transition-colors duration-300">
                We believe in creating an environment where talent thrives. Our
                culture is built on respect, collaboration, and continuous
                improvement.
              </p>
            </div>
            <div className="slide-right">
              <div className="grid sm:grid-cols-2 gap-4">
                {perks.map((perk, index) => (
                  <div
                    key={perk}
                    data-testid={`perk-${index}`}
                    className="flex items-center gap-3 glass-card p-4 rounded-lg magnetic-hover group"
                    style={{ animationDelay: `${index * 0.1}s` }}
                  >
                    <CheckCircle className="w-5 h-5 text-[#6DB432] flex-shrink-0 group-hover:scale-125 transition-transform duration-300" />
                    <span className="text-white text-sm group-hover:text-[#8BC064] transition-colors duration-300">{perk}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-[#353537]/50 relative overflow-hidden">
        {/* Animated background elements */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute top-1/4 left-1/4 w-32 h-32 border border-[#6DB432]/10 rounded-full animate-ping" style={{ animationDuration: '4s' }} />
          <div className="absolute bottom-1/4 right-1/4 w-24 h-24 border border-[#8BC064]/10 rounded-full animate-ping" style={{ animationDuration: '3s', animationDelay: '1s' }} />
        </div>
        
        {/* Particles */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="particle" style={{ top: '20%', left: '10%', animationDelay: '0s' }} />
          <div className="particle" style={{ top: '80%', left: '90%', animationDelay: '2s' }} />
        </div>
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="text-center zoom-reveal">
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4 hover:scale-105 transition-transform duration-300 inline-block">
              Ready to Start Your Journey?
            </h2>
            <p className="text-gray-400 text-lg mb-8 max-w-2xl mx-auto">
              Take the first step towards a rewarding career. Apply now and
              become part of the NUVEXA family.
            </p>
            <Link to="/apply">
              <Button
                data-testid="careers-cta-apply-btn"
                className="bg-[#6DB432] hover:bg-[#8BC064] text-white font-bold px-10 py-4 text-lg rounded-md btn-glow btn-shine rubberBand-hover animate-pulse-glow particle-burst"
              >
                Apply Now
                <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-2 transition-transform" />
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Careers;
